import {
  ADD_EVENT,
  DELETE_EVENT,
  RENAME_EVENT,
  EDIT_EVENT_DIRECTIONS,
  EDIT_EVENT_TEXT,
  EDIT_EVENT_HTML,
  REORDER_EVENTS,
} from '../../constants/ActionTypes';
import {
  spawnImageContent,
  spawnTextContent,
  spawnHtmlContent,
  spawnAudioContent,
  spawnChoiceContent,
  spawnStickerContent,
} from '../../constants/events/store';
import guid from '../../functions/id/guid';
import reorder from '../../functions/reorder';
import {
  findEvent,
  copyQuest,
  stateWithNextQuest,
  touchQuestModified,
} from '../questsHelpers';

/**
 * Handles event structure, lifecycle, text, html, directions and reordering.
 * @param {Object} state - current state of quests slice
 * @param {Object} action - dispatched Redux action
 * @returns {Object|null}
 */
export default function eventContentReducer(state, action) {
  let nextQuest;

  switch (action.type) {
    case ADD_EVENT: {
      nextQuest = copyQuest(state, action.questId);
      if (!nextQuest) return state;
      let order = nextQuest.events.length;
      if (typeof action.order === 'number') {
        order = action.order;
      } else if (nextQuest.events.length > 1) {
        order = nextQuest.events.length - 1;
      }
      nextQuest.events = nextQuest.events.map((e, i) =>
        i >= order ? { ...e, order: e.order + 1 } : e
      );
      nextQuest.events.splice(order, 0, {
        title: action.title,
        id: action.guid ? action.guid : guid(),
        order,
        trigger: action.time === 0 ? { time: 0 } : {},
        skip: {
          allow: true,
          penalty: 0,
        },
        nextButton: '',
        bonus: 0,
        content: [
          spawnImageContent(),
          spawnTextContent(),
          spawnHtmlContent(`<p>${action.html}</p>`),
          spawnAudioContent(),
          spawnChoiceContent(),
          spawnStickerContent(),
        ],
      });

      touchQuestModified(nextQuest);

      return {
        ...state,
        quests: state.quests.map((quest) =>
          quest.id === action.questId ? nextQuest : quest
        ),
      };
    }

    case DELETE_EVENT: {
      const targetQuest = (state.quests || []).find(
        (quest) => String(quest.id) === String(action.questId)
      );
      if (!targetQuest) return state;
      nextQuest = { ...targetQuest };
      nextQuest.events = nextQuest.events.filter(
        (event) => event.id !== action.eventId
      );

      nextQuest.events = nextQuest.events.map((event, i) => ({
        ...event,
        order: i,
      }));

      touchQuestModified(nextQuest);

      return {
        ...state,
        quests: state.quests.map((quest) =>
          quest.id === action.questId ? nextQuest : quest
        ),
      };
    }

    case RENAME_EVENT: {
      const targetQuest = (state.quests || []).find(
        (quest) => String(quest.id) === String(action.questId)
      );
      if (!targetQuest) return state;
      nextQuest = { ...targetQuest };
      const event = (nextQuest.events || []).find(
        (e) => String(e.id) === String(action.eventId)
      );
      if (event) {
        event.title = action.title;
      }

      touchQuestModified(nextQuest);

      return {
        ...state,
        quests: state.quests.map((quest) =>
          quest.id === action.questId ? nextQuest : quest
        ),
      };
    }

    case EDIT_EVENT_DIRECTIONS: {
      nextQuest = copyQuest(state, action.questId);
      if (!nextQuest) return state;
      const event = findEvent(nextQuest.events, action.eventId);
      if (!event) {
        return state;
      }

      const content = event.content || [];
      const dirContentIndex = content.findIndex(
        (item) => item.contentType === 'directions'
      );
      content[dirContentIndex > -1 ? dirContentIndex : content.length] = {
        contentType: 'directions',
        visible: action.visible,
        data: action.data,
      };
      event.content = [...content];

      touchQuestModified(nextQuest);

      return {
        ...state,
        quests: state.quests.map((quest) =>
          quest.id === action.questId ? nextQuest : quest
        ),
      };
    }

    case EDIT_EVENT_TEXT: {
      const targetQuest = (state.quests || []).find(
        (quest) => String(quest.id) === String(action.questId)
      );
      if (!targetQuest) return state;
      nextQuest = { ...targetQuest };
      const event = (nextQuest.events || []).find(
        (e) => String(e.id) === String(action.eventId)
      );
      if (!event) {
        return state;
      }
      const text = {
        contentType: 'text',
        visible: action.visible,
        data: action.text,
      };
      if (!event.content) {
        event.content = [text];
      } else {
        event.content = event.content.filter(
          (item) => item.contentType !== 'text'
        );
        event.content = [...event.content, text];
      }

      touchQuestModified(nextQuest);

      return {
        ...state,
        quests: state.quests.map((quest) =>
          quest.id === action.questId ? nextQuest : quest
        ),
      };
    }

    case EDIT_EVENT_HTML: {
      const index = (state.quests || []).findIndex(
        (quest) => quest.id === action.questId
      );
      if (index === -1) return state;

      const updatedQuest = { ...state.quests[index] };
      const updatedEvents = updatedQuest.events.map((event) =>
        event.id === action.eventId ? { ...event } : event
      );
      const eventIndex = updatedEvents.findIndex(
        (event) => event.id === action.eventId
      );
      if (eventIndex !== -1) {
        const updatedEvent = { ...updatedEvents[eventIndex] };
        const contentIndex = updatedEvent.content.findIndex(
          (item) => item.contentType === 'html'
        );

        if (contentIndex !== -1) {
          updatedEvent.content[contentIndex] = {
            ...updatedEvent.content[contentIndex],
            data: action.html,
            visible: action.visible,
            lastModified: new Date().toISOString(),
          };
        } else {
          updatedEvent.content.push({
            contentType: 'html',
            visible: action.visible,
            data: action.html,
            lastModified: new Date().toISOString(),
          });
        }

        updatedEvents[eventIndex] = updatedEvent;
      }

      updatedQuest.events = updatedEvents;
      touchQuestModified(updatedQuest);

      const updatedQuests = [...state.quests];
      updatedQuests[index] = updatedQuest;
      return {
        ...state,
        quests: updatedQuests,
      };
    }

    case REORDER_EVENTS: {
      const {
        ids: { questId },
        fromIndex,
        toIndex,
      } = action;
      nextQuest = copyQuest(state, questId);
      if (!nextQuest) return state;

      nextQuest.events = reorder(nextQuest.events, fromIndex, toIndex).map(
        (event, index, eventsList) => {
          const { order } = event;
          /* eslint-disable no-param-reassign */
          event.order = index;

          if (order === index) return event;

          if (index === 0) {
            event.trigger = { time: 0 };
          } else if (index === eventsList.length - 1) {
            event.content = (event.content || []).map((item) =>
              item.contentType === 'choice'
                ? {
                    contentType: 'choice',
                    data: {
                      options: [],
                      answer: -1,
                    },
                    visible: false,
                  }
                : item
            );
          } else if (order === 0) {
            event.trigger = {};
          } else {
            const prev = eventsList[index - 1];
            const choiceVisible = Boolean(
              prev.content.find(
                (item) => item.contentType === 'choice' && item.visible
              )
            );
            if (choiceVisible) {
              event.trigger = {};
            }
          }
          /* eslint-enable no-param-reassign */

          return event;
        }
      );

      return stateWithNextQuest(state, nextQuest);
    }

    default:
      return null;
  }
}
